import { useQuery } from "@tanstack/react-query";
import { saleApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsSaleI,
  SaleI,
  ReconcileSriResultI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getSales = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  sri,
}: PropsSaleI): Promise<ServiceResponseI<DataI<SaleI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    //headers: { 'Authorization': validateToken

    if (sri) {
      const { data } = await saleApi.get<ServiceResponseI<DataI<SaleI>>>(
        "list-sri",
        {
          params,
          headers: { Authorization: validateToken },
        },
      );
      return data;
    }
    const { data } = await saleApi.get<ServiceResponseI<DataI<SaleI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllSales = async (): Promise<ServiceResponseI<SaleI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await saleApi.get<ServiceResponseI<SaleI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Trae TODAS las ventas/facturas SRI de un rango de fechas (sin paginar),
 * para exportar a Excel — mismo patrón que
 * received-document/hook.ts getAllReceivedDocumentsByRange: primero pide el
 * total (limit:1) y luego lo trae todo en una sola página.
 */
export const getAllSalesByRange = async (
  dateFrom: string,
  dateTo: string,
): Promise<SaleI[]> => {
  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");

  const baseParams = { search: "", sorting: "[]", dateFrom, dateTo };

  const { data: firstPage } = await saleApi.get<
    ServiceResponseI<DataI<SaleI>>
  >("list-sri", {
    params: { page: 1, limit: 1, ...baseParams },
    headers: { Authorization: validateToken },
  });

  const total = firstPage?.data?.total ?? 0;
  if (total === 0) return [];

  const { data: allPage } = await saleApi.get<ServiceResponseI<DataI<SaleI>>>(
    "list-sri",
    {
      params: { page: 1, limit: total, ...baseParams },
      headers: { Authorization: validateToken },
    },
  );

  return allPage?.data?.dato ?? [];
};

export interface SaleSaveResultI {
  id: string;
  numberSale: string;
  claveAcceso: string | null;
  jobId: string | null;
}

export const salePost = async <T>(
  getForms: T,
): Promise<ServiceResponseI<SaleSaveResultI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await saleApi.post<ServiceResponseI<SaleSaveResultI>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const salePut = async <T>(
  id: string,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await saleApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const saleById = async (
  id: string,
): Promise<ServiceResponseI<SaleI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await saleApi.get<ServiceResponseI<SaleI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const saleConvertToInvoiceById = async (
  id: string,
  invoiceDate?: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await saleApi.get<ServiceResponseI<string>>(
      `/${id}/convert-to-invoice`,
      {
        headers: { Authorization: validateToken },
        params: invoiceDate ? { invoiceDate } : undefined,
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const saleDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await saleApi.delete<ServiceResponseI<string>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const saleVerifySri = async (
  id: string,
): Promise<ServiceResponseI<{ resultado: string; estadoSri?: string }>> => {
  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");
  const { data } = await saleApi.post(
    `/${id}/verificar-sri`,
    {},
    {
      headers: { Authorization: validateToken },
    },
  );
  return data;
};

/**
 * Reintenta la emisión electrónica de una venta cuya fila sale_sri quedó en
 * ERROR / sin clave de acceso (Magno rechazó el payload o el SRI estaba caído).
 */
export const saleReemitirSri = async (
  id: string,
): Promise<
  ServiceResponseI<{
    sriEstado: string;
    claveAcceso: string | null;
    error: string | null;
  }>
> => {
  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");
  const { data } = await saleApi.post(
    `/${id}/reemitir-sri`,
    {},
    {
      headers: { Authorization: validateToken },
    },
  );
  return data;
};

/**
 * "Corregir facturas": revisa las ventas del cliente sin claveAcceso local y
 * las busca en Magno por referenciaExterna, completando lo que encuentre.
 */
export const saleReconcileSri = async (): Promise<
  ServiceResponseI<ReconcileSriResultI>
> => {
  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");
  const { data } = await saleApi.post(
    "/reconcile-sri",
    {},
    {
      headers: { Authorization: validateToken },
    },
  );
  return data;
};

export const downloadFacturaPdf = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await saleApi.get(`/${id}/factura-pdf`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};

export const useSaleSri = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
}: PropsSaleI) => {
  const salesQuery = useQuery(
    [
      "sales-sri",
      { pagination, sorting, globalFilter, dateFrom, dateTo, sri: true },
    ],
    () =>
      getSales({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        sri: true,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    salesQuery,
  };
};

export const useSales = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
}: PropsSaleI) => {
  const salesQuery = useQuery(
    ["sales", { pagination, sorting, globalFilter, dateFrom, dateTo }],
    () => getSales({ pagination, sorting, globalFilter, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    salesQuery,
  };
};
